import { api } from "../../lib/api";
import { getGoogleMapsApiKey } from "../../lib/getEnvVariables";
export const getProjectListData = async (queryParams) => {
    const { data } = await api.post(`/projects/search${queryParams && "?" + queryParams}`);
    return data; // return the data if the API call succeeds
};
export const getProjectAssetsData = async (id, queryParams) => {
    const { data } = await api.post(`/projects/${id}/assets/search${queryParams && "?" + queryParams}`);
    return data;
};
export const getUsageHistoryDetailsData = async (id, startDate, aggregation, assetSourceType, assetId) => {
    const config = { params: { aggregation, assetSourceType, startDate, ...(assetId && { assetId }) } };
    const { data } = await api.get(`/projects/${id}/energy/histogram`, config);
    return data;
};
/**
 * Fetches productivity aggregated data (material moved, energy used, load count,
 * idle time and cycle-time activities) for a project from the shared energy
 * histogram endpoint using `aggregation=productivity`.
 *
 * @param id - Project id.
 * @param startDate - Selected date (yyyy-MM-dd).
 * @param duration - Aggregation window (hourly | daily | weekly | monthly | daywiseMonth).
 * @param assetSourceType - Asset source type (MACHINE | ESS).
 * @param assetId - Mandatory only when `duration` is `daywiseMonth`.
 */
export const getProjectProductivityData = async (id, startDate, duration, assetSourceType, assetId) => {
    if (duration === "daywiseMonth" && !assetId) {
        throw new Error('assetId is required when duration is "daywiseMonth"');
    }
    const config = {
        headers: { "x-api-version": "2.0" },
        params: {
            aggregation: "productivity",
            assetSourceType,
            duration,
            startDate,
            ...(assetId && { assetId })
        }
    };
    const { data } = await api.get(`/projects/${id}/energy/histogram`, config);
    return data;
};
export const getProjectEnergyMetricsData = async (id, startDate) => {
    const config = { params: { startDate } };
    const { data } = await api.get(`/projects/${id}/energy/metrics`, config);
    return data;
};
export const getProjectNetworkData = async (id) => {
    const { data } = await api.get(`/projects/${id}/network/connections`);
    return data;
};
export const getProjectEnergyActivitiesData = async (id, startDate) => {
    const config = { params: { startDate } };
    const { data } = await api.get(`/projects/${id}/energy/activities`, config);
    return data;
};
export const getProjectAssetChargeActivitiesData = async (id, startDate) => {
    const { data } = await api.post(`/projects/${id}/assets/events/search?startDate=${startDate}`);
    return data;
};
export const getProjectAssetChargeActivitiesDataWithAssetId = async (id, startDate, assetId) => {
    const requestParams = {
        filters: [
            {
                propertyName: "assetId",
                type: "stringEquals",
                values: [assetId]
            }
        ]
    };
    const { data } = await api.post(`/projects/${id}/assets/events/search?startDate=${startDate}`, requestParams);
    return data;
};
export const getMapDirectionData = async (routeSources) => {
    const config = {
        headers: {
            "X-Goog-Api-Key": getGoogleMapsApiKey(),
            "X-Goog-FieldMask": "routes.duration,routes.distanceMeters,routes.polyline.encodedPolyline,routes.legs"
        }
    };
    const routes = await Promise.all(routeSources.map(async (routeSource) => {
        const requestBody = JSON.stringify({
            destination: routeSource.destination,
            origin: routeSource.origin,
            travelMode: "DRIVE"
        });
        const { data } = await api.post(`https://routes.googleapis.com/directions/v2:computeRoutes`, requestBody, config);
        return {
            destAssetName: routeSource.destAssetName,
            id: routeSource.id,
            routeData: data.routes[0],
            srcAssetName: routeSource.srcAssetName,
            srcAssetNameFull: routeSource.srcAssetNameFull,
            srcAssetNameToOthers: routeSource.srcAssetNameToOthers
        };
    }));
    return { routes };
};
export const getAssetSocChartDetailsData = async (assetId) => {
    const { data } = await api.post(`/assets/${assetId}/idle/burndown`);
    return data;
};
export const getMethanolAssetEvents = async (projectId, assetId, fromDate, toDate, queryParams) => {
    const params = new URLSearchParams();
    if (queryParams?.startDate)
        params.set("startDate", queryParams.startDate);
    if (queryParams?.limit)
        params.set("limit", queryParams.limit);
    if (queryParams?.skip)
        params.set("skip", queryParams.skip);
    if (queryParams?.cursor)
        params.set("cursor", queryParams.cursor);
    const queryString = params.toString();
    const suffix = queryString ? `?${queryString}` : "";
    const url = `/projects/${projectId}/assets/events/search${suffix}`;
    const requestBody = {
        filters: [
            {
                propertyName: "assetId",
                type: "stringEquals",
                values: [assetId]
            },
            {
                propertyName: "timestamp",
                range: {
                    includeLeft: true,
                    includeRight: true,
                    left: fromDate,
                    right: toDate
                },
                type: "timeRange"
            }
        ]
    };
    const { data } = await api.post(url, requestBody, {
        headers: { "x-api-version": "2.0" }
    });
    return data;
};
